import {
  Component,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
} from '@stencil/core';
import {
  BasketHelper,
  BasketRepository,
  BasketModel,
  BasketService,
  ExtraBasketModel,
  Language,
  MONEY_SYMBOLS,
  QuoteCreateBody,
  QuoteModel,
  QuoteService,
  RateHelper,
  RoomBasketOccupancy,
  SessionHelper,
  SessionRepository,
  SessionModel,
  SessionService,
  RoomRepository,
} from '@cesare';
import { isEqual } from 'lodash-es';
import {
  catchError,
  switchMap,
  distinctUntilChanged,
  debounceTime,
  map,
  take,
  tap,
  from,
} from 'rxjs';
import {
  QwApiError,
  QwChangeExtraEvent,
  QwChangeRoomEvent,
  LOCATIONS,
  NEWSLETTERS,
  NEWSLETTER_LANGUAGES,
  NEWSLETTER_COUNTRIES,
  QwSessionSettings,
} from '../../index';
import { QwButton } from '../shared/qw-button/qw-button';

import { getQwRateCodeInfo } from '../qw-room-rate/qw-room-rate-model';
import {
  getSessionSettingsFromSessionStorage,
  sessionStorageGetItem,
  sessionStorageRemoveItem,
  sessionStorageSetItem,
  removeFusionIdFromSessionStorage,
  removeSessionSettingsFromSessionStorage,
} from '../../core/storage/storage.service';
import { getSessionLocationFromUrl } from '../../core/session/session.service';
import { isAccommodationSatisfied } from '../../core/basket/basket.service';
import { SessionHelperService } from '@cesare';
import { forkJoin, of, combineLatest } from 'rxjs';
import { getPromoCodeValueFromQueryParams } from '../../core/promo-code/promo-code.service';
import { loadingService } from '../../core/qw-loading-manager/qw-loading.service';
import { isRunningLocally } from '../../core/utils';
import {
  isBookingAccommodationSatisfied,
  isInCheckoutPage,
  isInExtraPage,
  isInRoomsPage,
  sessionUpdating$,
  setBookingInProgress,
  deleteUserSession,
  getLocationValueFromUrl,
} from '../../core/app/app';

interface Item {
  index: number;
  item_id: string;
  item_name: string;
  item_category: 'room' | 'extra';
  item_brand: 'JOE';
  price: number;
  quantity: number;
}

interface EcommerceGtmData {
  items: Item[];
  value: number;
  tax: number;
  currency: string;
  coupon: string;
  discount: number;
  transaction_id: string;
}

@Component({
  tag: 'qw-basket-summary',
  styleUrl: 'qw-basket-summary.scss',
  shadow: false,
})
export class QwBasketSummary {
  @Prop() qwBasketSummaryTitle: string;
  @Prop() qwBasketSummaryLocations: string = JSON.stringify(LOCATIONS);
  @Prop() qwBasketHasCustomTaxes: string;
  @Prop() currentLang: string = sessionStorageGetItem('lang_js') || 'en';
  @State() selectedLocationName: string;
  @State() formQuote: QuoteCreateBody;
  @State() quote: QuoteModel;
  @State() basket: BasketModel;
  @State() session: SessionModel;
  @State() componentLoading: boolean = true;
  @State() basketWorking: boolean = false;
  @State() formQuoteIsValid: boolean = false;
  @State() isAccommodationSatisfy: boolean = isBookingAccommodationSatisfied();
  @State() isDirectCheckout: boolean;
  @State() error: QwApiError;
  @State() renderTriggered: boolean = false;
  @State() sessionSettings: QwSessionSettings;
  @State() modalRoomsWarning: boolean = false;
  @State() firstLoad = false;
  @Event() qwBasketChange: EventEmitter<BasketModel>;
  @Event() redirectToHomepage: EventEmitter<void>;
  @Event() qwSessionChanged: EventEmitter<SessionModel>;
  @Event() qwOpenBookigModal: EventEmitter;
  @Event() qwScrollToTaxesDetails: EventEmitter;
  @Event() resetCheckboxRates: EventEmitter;
  @Event() qwEmitGtmData: EventEmitter<any>;
  @Event() qwEmitGtmDataPageView: EventEmitter<any>;
  @State() gtmFired: boolean = false;

  public componentWillLoad() {
    if (
      !isRunningLocally() &&
      getSessionLocationFromUrl(this.qwBasketSummaryLocations)?.name !==
        getSessionSettingsFromSessionStorage()?.location.name
    ) {
      removeFusionIdFromSessionStorage();
      removeSessionSettingsFromSessionStorage();
      SessionService.getSession()
        .pipe(distinctUntilChanged(isEqual))
        .subscribe((session) => {
          this.qwSessionChanged.emit(session);
          this.getSetPromoCodeValueFromUrl();
        });
    } else {
      // @ts-ignore
      SessionService.getSession()
        .pipe(distinctUntilChanged(isEqual))
        .subscribe(() => {
          this.getSetPromoCodeValueFromUrl();
        });
    }

    this.sessionSettings = getSessionSettingsFromSessionStorage();

    this.selectedLocationName =
      this.sessionSettings?.location?.name ||
      getLocationValueFromUrl() ||
      Language.getTranslation('pleaseSelect');

    SessionRepository.sessionLoaded$
      .pipe(
        distinctUntilChanged(isEqual),
        switchMap((session) => {
          this.session = session;
          return BasketService.getBasket(session);
        }),
        distinctUntilChanged(isEqual),
      )
      .subscribe();

    BasketRepository.basketWithPrice$
      .pipe(distinctUntilChanged(isEqual))
      .subscribe((basket) => {
        this.basket = basket;
        this.isAccommodationSatisfy = isAccommodationSatisfied(
          this.basket.rooms,
          { session: this.session },
        );
        this.redirectLogic();
        this.qwBasketChange.emit(basket);
        if (basket.rooms.length) {
          isInCheckoutPage() &&
            !this.gtmFired &&
            this.emitBasketGtmData(this.basket);
          // get first rate because in basket there can only be just one rate type
          const firstOccupancy = Object.values(
            basket.rooms[0].occupancies,
          )[0] as any;
          this.isDirectCheckout = getQwRateCodeInfo(
            firstOccupancy.rateInformation?.code,
          ).directCheckout;
        }
      });

    this.componentIsLoading$.pipe(distinctUntilChanged()).subscribe((value) => {
      this.componentLoading = value;
      // Avoid render on rooms page if redirect active
      this.isAccommodationSatisfy && isInRoomsPage()
        ? ''
        : this.setServiceLoading(this.componentLoading);
    });

    this.setServiceLoading(true);

    loadingService.loadComplete$.subscribe((res) => {
      this.renderTriggered = res;
      if (res) {
        this.firstLoad = true;
      }
    });
  }

  public componentIsLoading$ = combineLatest([
    SessionRepository.sessionIsLoading$,
    BasketRepository.basketIsLoading$,
    RoomRepository.roomIsLoading$,
    sessionUpdating$,
  ]).pipe(
    map(([sessionLoading, basketLoading, roomLoading, sessionUpdating]) => {
      return sessionLoading || basketLoading || roomLoading || sessionUpdating;
    }),
    debounceTime(0),
    distinctUntilChanged(),
  );

  private setServiceLoading(isLoading: boolean) {
    loadingService.setLoading({
      idComponent: 'basket-summary',
      isLoading,
    });
  }

  private getSetPromoCodeValueFromUrl() {
    const promoCodeValue = getPromoCodeValueFromQueryParams();
    if (promoCodeValue) {
      this.updateContextWithSessionSettings(promoCodeValue);
      sessionStorageSetItem('promoCode', promoCodeValue);
    }
  }

  private getPromoCode() {
    return this.session?.context?.promoCode;
  }

  private emitBasketGtmData(basket: BasketModel) {
    this.gtmFired = true;
    const data: EcommerceGtmData = {
      items: [],
      value: basket.price.totalPrice.converted.value.amount,
      tax:
        basket.taxes.onSite.value.amount + basket.taxes.excluded.value.amount,
      currency: basket.price.totalPrice.converted.value.currency,
      coupon: basket.price.offerCode?.code,
      discount: basket.price.discountedAmount.converted.value.amount,
      transaction_id: this.session.sessionId,
    };

    if (basket.rooms.length > 0) {
      basket.rooms.forEach((roomEl) => {
        const item: Item = {
          index: roomEl.roomId,
          item_id: roomEl.productId,
          item_name: roomEl.name,
          item_brand: 'JOE',
          item_category: 'room',
          price: Object.values(roomEl.occupancies)[0].price.converted.value
            .amount,
          quantity: Object.values(roomEl.occupancies)[0].selectedQuantity,
        };
        data.items.push(item);
      });
    }
    if (basket.hotelExtras.length > 0) {
      basket.hotelExtras.forEach((extraEl) => {
        const item: Item = {
          index: extraEl.extraId,
          item_id: extraEl.productId,
          item_name: extraEl.name,
          item_brand: 'JOE',
          item_category: 'extra',
          price: extraEl.price.converted.value.amount,
          quantity: extraEl.selectedQuantity.value,
        };
        data.items.push(item);
      });
    }
    this.qwEmitGtmDataPageView.emit(data);
  }

  private updateContextWithSessionSettings(promoCodeValue?: string) {
    return SessionHelperService.fetchUpdateSessionContext(
      this.session?.sessionId,
      {
        stayPeriod:
          getSessionSettingsFromSessionStorage()?.stayPeriod ||
          this.session.context.stayPeriod,
        guests:
          getSessionSettingsFromSessionStorage()?.guests ||
          this.session.context.guests,
        selectedHotelId:
          getSessionSettingsFromSessionStorage().location?.hotelId ||
          this.session.context.selectedHotelId,
        promoCode: promoCodeValue,
      },
    )
      .pipe(
        take(1),
        switchMap((res) => {
          return forkJoin([
            of(res),
            SessionService.fetchSession(this.session.sessionId),
          ]);
        }),
        catchError((err) => {
          this.error = err;
          return of(err);
        }),
      )
      .subscribe();
  }

  @Listen('qwBookQuote', { target: 'window' })
  onQwBookQuote(e: CustomEvent<QuoteModel>) {
    this.quote = e.detail;
  }

  @Listen('qwBookFormQuoteChange', { target: 'window' })
  onQwBookFormQuoteChange(e: CustomEvent<QuoteCreateBody>) {
    this.formQuote = e.detail;
  }

  @Listen('qwBookFormQuoteIsValid', { target: 'window' })
  onQwBookFormQuoteIsValid(e: CustomEvent<boolean>) {
    this.formQuoteIsValid = e.detail;
  }

  private getQuotePaymentModes() {
    return this.quote.paymentModes.find((mode) => mode.supportedCardTypes)
      .supportedCardTypes;
  }

  private getTotalPrice(basketRoomOccupancy: RoomBasketOccupancy) {
    if (!basketRoomOccupancy?.price?.converted) {
      return '';
    }
    return RateHelper.multiplyMoney(
      basketRoomOccupancy?.price?.converted,
      basketRoomOccupancy?.selectedQuantity,
    );
  }

  setRoomInBasket(e: QwChangeRoomEvent, options?: { singleRemove: boolean }) {
    const occId = BasketHelper.getFirstOccupancyIdInBasketRoom(e.room);
    const { rateId, occupancyId } = e.room.occupancies[occId];
    this.setServiceLoading(true);
    this.basketWorking = true;
    BasketService.setRoomInBasket({
      quantity: parseInt(e.quantity),
      roomId: e.room.roomId,
      rateId: e.rateId || rateId,
      occupancyId: e.occupancyId || occupancyId,
    })
      .pipe(
        take(1),
        tap((basket) => {
          this.resetCheckboxRates.emit();
          this.qwBasketChange.emit(basket as BasketModel);
          this.isAccommodationSatisfy = isAccommodationSatisfied(
            this.basket.rooms,
            { session: this.session },
          );
          if (this.isAccommodationSatisfy) {
            setBookingInProgress({
              isAccommodationSatisfied: true,
              isInProgress: true,
            });
          } else {
            setBookingInProgress({
              isAccommodationSatisfied: false,
              isInProgress: basket.rooms.length > 0 ? true : false,
            });
          }
          if (options.singleRemove && !!this.session.context.promoCode) {
            // hack inserito perché le rate si rompono se si rimuove una room ed è presente il promocode
            window.location.reload();
          }
          this.redirectLogic();
          SessionService.fetchSession(this.session.sessionId, {
            preventShare: true,
          });
        }),
      )
      .subscribe();
  }

  setExtraInBasket(e: QwChangeExtraEvent) {
    this.setServiceLoading(true);
    this.basketWorking = true;
    BasketService.setExtraInBasket({
      extraId: e.extraId,
      quantity: parseInt(e.quantity),
      roomId: e.roomId,
    }).subscribe(() => {
      this.setServiceLoading(false);
      this.basketWorking = false;
    });
  }

  private cleanBasketRoomsExtras() {
    BasketService.deleteBasket().subscribe(() => {
      this.redirectLogic();
    });
  }

  private formatSessionGuests() {
    const adultsNumber = this.session.context.guests.adults;
    const childrenNumber = this.session.context.guests.children;
    const infantsNumber = this.session.context.guests.infants;

    const adultsLabel =
      adultsNumber > 1
        ? Language.getTranslation('adults')
        : Language.getTranslation('adult');
    const childrenLabel =
      childrenNumber > 1
        ? Language.getTranslation('children')
        : Language.getTranslation('child');

    const guests = `
      ${adultsNumber} ${adultsLabel}${childrenNumber > 0 ? ',' : ''}
      ${childrenNumber > 0 ? ' ' + childrenNumber + ' ' + childrenLabel : ''}${
        childrenNumber > 0 ? ',' : ''
      }
      ${
        infantsNumber > 0
          ? ' ' + infantsNumber + ' ' + Language.getTranslation('infants')
          : ''
      }
    `;
    return guests;
  }

  private goToPage(pagename: string) {
    this.basketWorking = true;
    this.componentLoading = true;
    if (!isRunningLocally()) {
      window.location.href = `/${this.sessionSettings.location.value}/${this.currentLang}/${pagename}`;
    } else {
      window.location.href = `/pages/${pagename}`;
    }
  }

  private goToCheckout() {
    this.basketWorking = true;
    if (this.isAccommodationSatisfy) {
      this.goToPage('checkout');
    }
  }

  private goToExtras() {
    this.basketWorking = true;
    if (this.isAccommodationSatisfy) {
      this.goToPage('extras');
    }
  }

  private redirectLogic() {
    if (this.isAccommodationSatisfy && isInRoomsPage()) {
      this.basketWorking = true;
      this.goToPage('extras');
    } else if (
      !this.isAccommodationSatisfy &&
      (isInExtraPage() || isInCheckoutPage())
    ) {
      this.basketWorking = true;
      this.goToPage('rooms');
    } else {
      this.basketWorking = false;
    }
  }

  public payNow = () => {
    let windowReference: any = window.open();
    QuoteService.createQuote(this.session.sessionId, this.formQuote)
      .pipe(
        tap((res) => {
          let url = res.redirectionUrl;
          if (typeof window.QW_HOTEL_ENV.LINK_DECORATOR_FN === 'function') {
            url = window.QW_HOTEL_ENV.LINK_DECORATOR_FN(url);
          }
          windowReference.location = url;
          this.setServiceLoading(true);
        }),
        switchMap(() => {
          if (this.formQuote.customerDetails.marketingConsent) {
            console.log('Marketing consent  given. Send newsletter');
            return from(this.sendToNewsletterApi(this.formQuote));
          } else {
            console.log('Marketing consent not given. Skipping newsletter.');
            return of({ success: false });
          }
        }),
        tap((newsletterResponse) => {
          if (newsletterResponse.success) {
            console.log('Newsletter Sent');
          } else {
            console.log('Newsletter Failed');
          }
          this.deleteUserSessionGoHome();
        }),
      )
      .subscribe();
  };

  public deleteUserSessionGoHome() {
    setTimeout(() => {
      deleteUserSession(this.session.sessionId, () => {
        sessionStorage.removeItem('bookingProgress');
        window.location.assign(window.location.origin);
      });
    }, 7000);
  }

  public async sendToNewsletterApi(data): Promise<{ success: boolean }> {
    const propertyNewsletterData =
      NEWSLETTERS[this.sessionSettings.location.value];
    if (!propertyNewsletterData) {
      console.log('Missing ids');
      return { success: false };
    }

    const baseUrl = `https://widgets.experience-hotel.com/front/widget/widget-newsletter/${propertyNewsletterData.widgetId}`;

    // Use URLSearchParams to handle the parameters
    const params = new URLSearchParams({
      callback: 'callback',
      'selected_lists[]': propertyNewsletterData.listId.toString(),
      'fields[email]': decodeURIComponent(data.customerDetails.emailAddress),
      'fields[firstname]': data.customerDetails.firstName,
      'fields[lastname]': data.customerDetails.lastName,
      'fields[country]':
        NEWSLETTER_COUNTRIES[data.customerDetails.countryCode].toString(),
      'fields[lang_id]': NEWSLETTER_LANGUAGES[this.currentLang].toString(),
    });

    if (data.customerDetails.phoneNumber) {
      params.append(
        'fields[mobile_formatted]',
        `${data.customerDetails.phoneCountryCode}${data.customerDetails.phoneNumber}`,
      );
    }

    // if (data.guestDetails[0].firstName && data.guestDetails.length) {
    //   const titles = data.guestDetails
    //     .map((guest) => `${guest.title} ${guest.firstName} ${guest.lastName}`)
    //     .join(', ');
    //   params.append('fields[title]', titles);
    // } else if (data.customerDetails.title) {
    //   params.append(
    //     'fields[title]',
    //     `${data.customerDetails.title} ${data.customerDetails.firstName} ${data.customerDetails.lastName}`,
    //   );
    // }

    const url = `${baseUrl}?${params.toString()}`;

    try {
      const response = await fetch(url, { method: 'GET' });
      if (response.ok) {
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      return { success: false };
    }
  }

  public emptyBasket() {
    this.basket.rooms.forEach((r) => {
      this.resetBookingData();
      const basketRoomOccupancies = Object.values(r.occupancies);
      basketRoomOccupancies.forEach((o) =>
        this.setRoomInBasket({
          quantity: '0',
          room: r,
          occupancyId: o.occupancyId,
        }),
      );
    });
  }

  public openModal() {
    this.qwOpenBookigModal.emit();
  }

  private fetchSessionAfterRemovingPromocode() {
    return SessionHelperService.fetchUpdateSessionContext(
      this.session?.sessionId,
      {
        stayPeriod: this.session.context.stayPeriod,
        guests: this.session.context.guests,
        selectedHotelId: this.session.context.selectedHotelId,
        promoCode: '',
      },
    )
      .pipe(
        switchMap((res) => {
          return forkJoin([
            of(res),
            SessionService.fetchSession(this.session.sessionId),
          ]);
        }),
        catchError((err) => of(err)),
      )
      .subscribe(() => {
        window.location.reload();
      });
  }

  public removePromoCode() {
    sessionStorageRemoveItem('promoCode');
    BasketService.deleteBasket().subscribe({
      next: () => {
        this.fetchSessionAfterRemovingPromocode();
      },
    });
  }

  private resetBookingData() {
    this.isAccommodationSatisfy = false;
    setBookingInProgress({
      isInProgress: false,
      isAccommodationSatisfied: false,
    });
  }

  public getGuestNumber(occupancyValue) {
    let asArray = Object.entries(occupancyValue);
    let guestNumber = asArray.filter(
      ([, value]) => value !== 0 && value !== true && value != false,
    );
    return Number(guestNumber[0][1]);
  }

  public emitEventScroll(e: Event) {
    e.preventDefault();
    this.qwScrollToTaxesDetails.emit();
  }

  @Listen('pageshow', { target: 'window' })
  public onWarningActive(event) {
    let historyTraversal =
      event.persisted ||
      (typeof window.performance != 'undefined' &&
        window.performance.navigation.type === 2);
    if (historyTraversal) {
      this.redirectLogic();
    }
  }

  @Listen('timeline', { target: 'window' })
  public onNavigationClicked(event: CustomEvent<string>) {
    let pageNavigation = event.detail;
    if (pageNavigation == 'checkout' || pageNavigation == 'extras') {
      window.location.href =
        '/' +
        this.sessionSettings.location.value +
        '/' +
        this.currentLang +
        '/' +
        pageNavigation;
    } else if (pageNavigation == 'rooms') {
      this.modalRoomsWarning = true;
    }
  }

  render() {
    const dataExtraSkeleton = [
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '22px',
        qwType: 'title',
      },
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '28px',
        qwType: 'select',
      },
    ];
    const dataExtraSkeleton1 = [
      {
        qwRepeat: 1,
        qwWidth: '30%',
        qwHeight: '20px',
        qwType: 'text',
      },
    ];
    const dataExtraSkeleton2 = [
      {
        qwRepeat: 2,
        qwWidth: '40%',
        qwHeight: '24px',
        qwType: 'title',
      },
    ];
    const dataExtraSkeleton3 = [
      {
        qwRepeat: 2,
        qwWidth: '25%',
        qwHeight: '22px',
        qwType: 'text',
      },
    ];
    const dataExtraSkeleton4 = [
      {
        qwRepeat: 2,
        qwWidth: '15%',
        qwHeight: '22px',
        qwType: 'title',
      },
    ];
    const dataExtraSkeleton5 = [
      {
        qwRepeat: 1,
        qwWidth: '90%',
        qwHeight: '45px',
        qwType: 'select',
      },
    ];
    return (
      <Host
        class={`${this.firstLoad ? 'visualize-basket' : 'loading-skeleton'}
          ${this.componentLoading || this.basketWorking ? 'qw-basket-summary--loading' : ''}
        `}
      >
        <div class="basket-skeleton">
          <qw-skeleton
            class="qw-skeleton-head"
            qwData={dataExtraSkeleton}
          ></qw-skeleton>
          <qw-skeleton
            class="qw-skeleton-basket"
            qwData={dataExtraSkeleton1}
          ></qw-skeleton>
          <qw-skeleton
            class="qw-skeleton-basket"
            qwData={dataExtraSkeleton2}
          ></qw-skeleton>
          <qw-skeleton
            class="qw-skeleton-basket"
            qwData={dataExtraSkeleton3}
          ></qw-skeleton>
          <qw-skeleton
            class="qw-skeleton-basket"
            qwData={dataExtraSkeleton4}
          ></qw-skeleton>
          <qw-skeleton
            class="qw-skeleton-basket"
            qwData={dataExtraSkeleton5}
          ></qw-skeleton>
        </div>
        {this.error && (
          <qw-error position="fixed">
            <div class="qw-error__overlay" />
            <div class="qw-error__content">
              <p>{this.error.body.Message}</p>
            </div>
          </qw-error>
        )}
        {(this.componentLoading || this.basketWorking) && (
          <div class="qw-loader"></div>
        )}
        <div class="qw-basket-summary__heading">
          <h3>{this.qwBasketSummaryTitle}</h3>
          <div class="qw-basket-summary__actions">
            {isInRoomsPage() && (
              <QwButton
                QwButtonDisabled={!this.isAccommodationSatisfy}
                QwButtonLabel={Language.getTranslation('continueBE')}
                QwButtonOnClick={() => this.goToExtras()}
              />
            )}
            {isInExtraPage() && (
              <QwButton
                QwButtonDisabled={!this.isAccommodationSatisfy}
                QwButtonLabel={Language.getTranslation('continueBE')}
                QwButtonOnClick={() => this.goToCheckout()}
              />
            )}
          </div>
          {isInCheckoutPage() && (
            <div class="qw-basket-summary__payment">
              <p class="qw-basket-summary__redirection-label">
                {Language.getTranslation('resetCartWarning')}
              </p>
              <QwButton
                QwButtonDisabled={!this.formQuoteIsValid}
                QwButtonLabel={
                  this.isDirectCheckout
                    ? Language.getTranslation('payNow')
                    : Language.getTranslation('confirm')
                }
                QwButtonOnClick={() => this.payNow()}
              />
            </div>
          )}
        </div>
        <div class="qw-basket-summary__content">
          <div class="qw-basket-summary__general-info">
            <p>
              {Language.getTranslation('destination')} &{' '}
              {Language.getTranslation('dates')}
            </p>
            <h3>
              <span>{this.selectedLocationName}</span>{' '}
              <span>
                {this.session && SessionHelper.formatStayPeriod(this.session)}
                <div
                  class="icon"
                  onClick={() => {
                    this.openModal();
                    this.qwEmitGtmData.emit({
                      event: 'GA4event',
                      eventName: 'bloc_interact',
                      event_data: {
                        bloc_name: 'your booking bar',
                        bloc_intercation: 'modify',
                      },
                    });
                  }}
                ></div>
              </span>
            </h3>
            <div class="session-info">
              <p class="session-info__guests">
                {this.session && this.formatSessionGuests()}
              </p>
            </div>
          </div>
        </div>
        <qw-currency />
        {!this.isAccommodationSatisfy &&
          !this.basketWorking &&
          this.basket?.rooms.length > 0 && (
            <p class="qw-basket-summary__warning">
              {Language.getTranslation('oneOrMoreGuestNeedsToBeAccommodated')}
            </p>
          )}
        <div class="qw-basket-summary__rooms">
          {this.basket?.rooms.map((basketRoom) => {
            const basketRoomOccupanciesArray = Object.values(
              basketRoom?.occupancies || {},
            );
            const basketRoomExtras: ExtraBasketModel[] = Object.values(
              basketRoom.extras,
            );

            return (
              <div class="qw-basket-summary__room--wrapper">
                <p>{Language.getTranslation('room')}</p>
                {basketRoomOccupanciesArray.map((occupancy) => {
                  return (
                    <div class="qw-basket-summary__room">
                      <div class="qw-basket-summary__room-name">
                        <div class="qw-basket-summary__room-title">
                          {occupancy.selectedQuantity}x {basketRoom.name}
                        </div>
                        <p class="qw-basket-summary__rate-info">
                          {occupancy?.rateInformation?.name} -{' '}
                          <span class="qw-basket-summary__rate-info--guest">
                            {this.getGuestNumber(occupancy.definition.value)}{' '}
                            {this.getGuestNumber(occupancy.definition.value) ==
                            1
                              ? Language.getTranslation('guest')
                              : Language.getTranslation('guests')}
                          </span>
                        </p>
                      </div>
                      <div class="qw-basket-summary__room-delete">
                        <div class="qw-basket-summary__room-price">
                          {this.getTotalPrice(occupancy)}
                        </div>
                        <QwButton
                          QwButtonClass="qw-button--delete"
                          QwButtonLabel=""
                          QwButtonOnClick={() => {
                            this.setRoomInBasket(
                              {
                                quantity: '0',
                                room: basketRoom,
                                occupancyId: occupancy.occupancyId,
                              },
                              { singleRemove: true },
                            );
                          }}
                        />
                      </div>
                    </div>
                  );
                })}

                {!!basketRoomExtras.length && (
                  <div class="qw-basket-summary__extras">
                    <p class="qw-basket-summary__section-title">
                      {Language.getTranslation('extras')}
                    </p>
                    {basketRoomExtras &&
                      basketRoomExtras.map((extra) => {
                        return (
                          <div class="qw-basket-summary__room qw-basket-summary__extra">
                            <div class="qw-basket-summary__room-name">
                              <div class="qw-basket-summary__room-title">
                                {extra.selectedQuantity.value}x {extra.name}
                              </div>
                            </div>
                            <div class="qw-basket-summary__room-delete">
                              <div class="qw-basket-summary__room-price">
                                {extra.price.converted.text
                                  ? RateHelper.multiplyMoney(
                                      extra.price.converted,
                                      extra.selectedQuantity.value,
                                    )
                                  : extra.gratuitousnessType.text}
                              </div>
                              <QwButton
                                QwButtonClass="qw-button--delete"
                                QwButtonLabel=""
                                QwButtonOnClick={() =>
                                  this.setExtraInBasket({
                                    quantity: '0',
                                    extraId: extra.extraId,
                                    ...(basketRoom.roomId && {
                                      roomId: basketRoom.roomId,
                                    }),
                                  })
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
          {!this.componentLoading && !this.basket?.rooms.length && (
            <p class="qw-basket-summary__no-rooms">
              {Language.getTranslation('noRooms')}
            </p>
          )}

          {this.basket &&
            this.basket.hotelExtras.map((extra) => {
              return (
                <div class="qw-basket-summary__extras--wrapper">
                  <p class="qw-basket-summary__section-title">
                    {Language.getTranslation('extras')}
                  </p>
                  <div class="qw-basket-summary__room qw-basket-summary__extra">
                    <div class="qw-basket-summary__room-name">
                      <div class="qw-basket-summary__room-title">
                        {extra.selectedQuantity.value}x {extra.name}
                      </div>
                    </div>
                    <div class="qw-basket-summary__room-delete">
                      <div class="qw-basket-summary__room-price">
                        {extra.price.converted.text
                          ? RateHelper.multiplyMoney(
                              extra.price.converted,
                              extra.selectedQuantity.value,
                            )
                          : extra.gratuitousnessType.text}
                      </div>

                      <QwButton
                        QwButtonClass="qw-button--delete"
                        QwButtonLabel=""
                        QwButtonOnClick={() =>
                          this.setExtraInBasket({
                            quantity: '0',
                            extraId: extra.extraId,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        {!!this.getPromoCode() && (
          <div class="qw-basket-summary__promo">
            <div class="promo-code">
              <p class="promo-code__title">
                {Language.getTranslation('promoCodeAdded')}
              </p>
              <div class="promo-code__value">
                <p>{this.getPromoCode()}</p>
                <p class="promo-code__amount">
                  {this.basket?.price?.discountedAmount?.converted?.value
                    ?.amount !== 0 && (
                    <span>
                      {
                        this.basket?.price?.discountedAmount?.converted.value
                          .amount
                      }{' '}
                    </span>
                  )}
                  {MONEY_SYMBOLS[
                    this.basket?.price?.discountedAmount?.converted.value
                      .currency
                  ] ||
                    this.basket?.price?.discountedAmount?.converted.value
                      .currency}
                  <QwButton
                    QwButtonClass="qw-button--delete"
                    QwButtonLabel=""
                    QwButtonOnClick={() => this.removePromoCode()}
                  />
                </p>
              </div>
            </div>
          </div>
        )}
        <qw-basket
          qw-basket-show-taxes="true"
          qw-basket-show-on-site-taxes="true"
          qw-basket-show-empty-button="false"
          qw-basket-show-price-if-empty="true"
          qw-basket-show-book-now-button={false}
          qw-basket-custom-taxes={this.qwBasketHasCustomTaxes}
        />
        {isInCheckoutPage() && (
          <div
            class="qw-basket--taxes-info"
            onClick={(e) => this.emitEventScroll(e)}
          >
            {Language.getTranslation('moreInformation')}
          </div>
        )}
        {isInCheckoutPage() && (
          <div class="qw-basket-summary__payment">
            <p class="qw-basket-summary__redirection-label">
              {Language.getTranslation('resetCartWarning')}
            </p>
            <QwButton
              QwButtonDisabled={!this.formQuoteIsValid}
              QwButtonLabel={
                this.isDirectCheckout
                  ? Language.getTranslation('payNow')
                  : Language.getTranslation('confirm')
              }
              QwButtonOnClick={() => this.payNow()}
            />
          </div>
        )}
        <div class="qw-basket-summary__actions">
          {isInRoomsPage() && (
            <QwButton
              QwButtonDisabled={!this.isAccommodationSatisfy}
              QwButtonLabel={Language.getTranslation('continueBE')}
              QwButtonOnClick={() => this.goToExtras()}
            />
          )}
          {isInExtraPage() && (
            <QwButton
              QwButtonDisabled={!this.isAccommodationSatisfy}
              QwButtonLabel={Language.getTranslation('continueBE')}
              QwButtonOnClick={() => this.goToCheckout()}
            />
          )}
        </div>
        {isInCheckoutPage() && (
          <p class="redirectPayment">
            {Language.getTranslation('redirectionPayment')}
          </p>
        )}
        {isInCheckoutPage() && (
          <div class="qw-basket-summary__payment-methods-wrapper">
            <p class="qw-basket-summary__redirection-label">
              {Language.getTranslation('secureTransaction')}
            </p>
            <div class="qw-basket-summary__payment-methods">
              {this.quote &&
                this.getQuotePaymentModes().map((m) => {
                  return (
                    <span
                      class={`qw-basket-summary__method qw-basket-summary__method--${m}`}
                    />
                  );
                })}
            </div>
          </div>
        )}
        {this.modalRoomsWarning && (
          <div class="qw-modal--warning">
            <div class="qw-modal--warning__detail-content">
              <span
                class="close-icon"
                onClick={() =>
                  (this.modalRoomsWarning = !this.modalRoomsWarning)
                }
              />
              <div class="qw-modal--warning__content">
                <h4>{Language.getTranslation('backToRoomAlert')}</h4>
                <div class="qw-modal--warning__buttons">
                  <QwButton
                    QwButtonLabel={Language.getTranslation('backToRoomList')}
                    QwButtonOnClick={() => this.cleanBasketRoomsExtras()}
                  />
                  <QwButton
                    QwButtonLabel={Language.getTranslation('continueBE')}
                    QwButtonOnClick={() =>
                      (this.modalRoomsWarning = !this.modalRoomsWarning)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </Host>
    );
  }
}
